import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import MobileWarehouseMap from './MobileWarehouseMap';
import ProductAutocomplete from '../../components/common/ProductAutocomplete';
import { Html5Qrcode } from 'html5-qrcode';
import { useUI } from '../../context/UIContext';

export default function MobileWarehouse() {
    const { user } = useAuth();
    const { showToast } = useUI();
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchSku, setSearchSku] = useState('');
    const [isScanning, setIsScanning] = useState(false);
    const scannerRef = useRef(null);

    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const res = await fetch(`/api/warehouses?companyId=${user.companyId}`);
                if (res.ok) {
                    const data = await res.json();
                    setWarehouses(data);

                    // Auto-select first warehouse from user settings if available
                    const userWarehouseIds = (user.warehouses || []).map(w => w.id);
                    if (userWarehouseIds.length > 0) {
                        setSelectedWarehouseId(userWarehouseIds[0].toString());
                    } else if (data.length > 0) {
                        setSelectedWarehouseId(data[0].id.toString());
                    }
                }
            } catch (err) {
                console.error("Failed to fetch warehouses", err);
            } finally {
                setLoading(false);
            }
        };
        fetchWarehouses();
    }, [user.companyId, user.warehouses]);

    const startScanner = async () => {
        setIsScanning(true);
        setTimeout(async () => {
            try {
                scannerRef.current = new Html5Qrcode("mobile-renderer");
                await scannerRef.current.start(
                    { facingMode: "environment" },
                    { fps: 10, qrbox: { width: 250, height: 250 } },
                    (decodedText) => {
                        setSearchSku(decodedText);
                        stopScanner();
                        showToast("Produit scanné : " + decodedText, "success");
                    },
                    () => { }
                );
            } catch (err) {
                console.error(err);
                showToast("Erreur caméra", "error");
                setIsScanning(false);
            }
        }, 300);
    };

    const stopScanner = async () => {
        if (scannerRef.current && scannerRef.current.isScanning) {
            await scannerRef.current.stop();
        }
        setIsScanning(false);
    };

    return (
        <div className="space-y-6">
            {/* Header section with info */}
            <div className="bg-gradient-to-br from-primary/10 to-blue-500/10 p-5 rounded-2xl border border-primary/20">
                <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center shrink-0 shadow-lg shadow-primary/30">
                        <span className="material-symbols-outlined text-2xl">map</span>
                    </div>
                    <div>
                        <h1 className="font-black text-xl text-slate-900 dark:text-white uppercase tracking-tight">Plan Dynamique</h1>
                        <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Visualisez vos stocks spatialement</p>
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="relative">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-primary text-sm">warehouse</span>
                        <select
                            value={selectedWarehouseId}
                            onChange={(e) => setSelectedWarehouseId(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all appearance-none font-bold text-sm shadow-sm"
                        >
                            <option value="">Sélectionner un entrepôt</option>
                            {warehouses.map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                        <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">expand_more</span>
                    </div>

                    <div className="flex gap-2">
                        <div className="flex-1">
                            <ProductAutocomplete
                                value={searchSku}
                                onChange={(val) => setSearchSku(val)}
                                onSelect={(p) => setSearchSku(p.name)}
                                companyId={user?.companyId}
                                warehouseId={selectedWarehouseId}
                                placeholder="Rechercher / Sélectionner..."
                            />
                        </div>
                        <button
                            onClick={startScanner}
                            className="w-12 h-12 flex items-center justify-center bg-white dark:bg-slate-800 border-2 border-primary/20 rounded-xl text-primary shadow-sm active:scale-90 transition-transform"
                        >
                            <span className="material-symbols-outlined">qr_code_scanner</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Scanner Overlay */}
            {isScanning && (
                <div className="fixed inset-0 z-[100] bg-black flex flex-col">
                    <div className="p-6 flex justify-between items-center bg-black/50 text-white">
                        <h3 className="font-bold">Scanner un produit</h3>
                        <button onClick={stopScanner} className="p-2 bg-white/10 rounded-full">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                    <div id="mobile-renderer" className="flex-1"></div>
                    <div className="p-8 bg-black/50 text-white text-center">
                        <p className="text-xs font-bold uppercase tracking-widest opacity-70">Placez le code QR dans le cadre</p>
                    </div>
                </div>
            )}

            {/* Main Map Area */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                    <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Chargement des données...</p>
                </div>
            ) : selectedWarehouseId ? (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                    <MobileWarehouseMap warehouseId={selectedWarehouseId} searchQuery={searchSku} />
                </div>
            ) : (
                <div className="p-12 text-center bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800">
                    <span className="material-symbols-outlined text-5xl text-slate-300 mb-4">warehouse</span>
                    <p className="text-slate-500 font-bold">Veuillez sélectionner un entrepôt pour afficher le plan.</p>
                </div>
            )}

            {/* Help / Tips */}
            <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-800/30 rounded-2xl flex gap-3">
                <span className="material-symbols-outlined text-orange-500 shrink-0">lightbulb</span>
                <p className="text-[11px] leading-relaxed text-orange-800 dark:text-orange-300 font-medium">
                    Astuce : Tapez sur un Rack (étagère) pour voir les produits qu'il contient. Le plan est optimisé pour un défilement vertical fluide.
                </p>
            </div>
        </div>
    );
}
